import { IconInfo24 } from '@warp-ds/icons/react';
import { Box, Button } from '@warp-ds/react';
import { shallowEqual, useSelector } from 'react-redux';

import { ImageGallery } from 'shared/components/imageGallery/ImageGallery';
import { useConfig } from 'shared/config/ConfigContext';
import { useBreakpoints } from 'shared/hooks/useBreakpoints';

import { Breadcrumbs } from '@/components/breadcrumbs/Breadcrumbs';
import { HotelEditPanel } from '@/components/hotel/HotelEditPanel';
import { useUnleash } from '@/hooks/unleash';
import { useHotelResults } from '@/hooks/useHotelResults';
import { useInitialImageIndex } from '@/hooks/useInitialImageIndex';

import { ApiActionStatus, type HotelOffer, type PaleState, PRICE_MIGHT_VARY_WARNING } from '@/interfaces';

import { formatDistances, getAmenitiesText } from '@/lib/utils/hotelData';
import { useTrackHotelPage } from '@/lib/utils/tracking';

import HotelEmptyWarning from '../emptyWarning/HotelEmptyWarning';
import { StarRating } from '../resultList/components/StarRating';
import { HotelResultItem } from './HotelResultItem';
import { NoHotelWarning } from './NoHotelWarning';

export const HotelPageLayout = () => {
    const { hotel, filters } = useSelector(
        (state: PaleState) => ({
            hotel: state.hotel,
            filters: state.filters,
        }),
        shallowEqual,
    );

    const { offers, status, offersLeft, fetchMoreOffers } = useHotelResults();
    const { imgPath } = useConfig();
    const isMobile = useBreakpoints({ breakpoint: 'md' });
    const initialImage = useInitialImageIndex();
    const unleash = useUnleash();

    const hasHotel = Boolean(hotel?.id);
    const isPending = status === ApiActionStatus.PENDING;

    useTrackHotelPage(hotel);

    if (!hasHotel) {
        return <NoHotelWarning filters={filters} />;
    }

    const amenities = getAmenitiesText(hotel);
    const destinationCollection = [hotel.destination, hotel.region, hotel.country].filter(Boolean).join(' / ');
    const images = (hotel.imagePaths ?? []).map((p) => ({ uri: `${imgPath}1280w${p}`, width: 1280, height: 720 }));
    const paxEnabled = unleash?.numPax;
    const shouldShowPriceWarning = filters.agesOfChildren?.some((age: number) => age > 12 || age < 2);

    return (
        <div className="flex flex-col gap-16 mb-32 pt-16">
            <Breadcrumbs data-testid="topBar" vertical="pp" hotelName={hotel.name} filters={filters} />

            <ImageGallery images={images} initialImage={initialImage} roundAllCorners showDescription={false} isMobile={isMobile} />

            <section className="pb-16" data-testid="hotelDescription">
                <p className="s-text-subtle">{destinationCollection}</p>
                <h1 className="py-8">{hotel.name}</h1>
                <StarRating id="hotelRating" rating={hotel.rating} emptyStars />

                <p className="pt-12">{formatDistances(hotel.distanceToBeach, hotel.distanceToCity)}</p>

                {amenities && <p className="s-text-subtle">{amenities}</p>}
            </section>

            <HotelEditPanel />

            {shouldShowPriceWarning && paxEnabled && (
                <Box info>
                    <div className="flex space-x-16">
                        <IconInfo24 className="s-text-link mt-2" />
                        <p>{PRICE_MIGHT_VARY_WARNING}</p>
                    </div>
                </Box>
            )}

            {offers.length <= 0 ? (
                <HotelEmptyWarning />
            ) : (
                offers.map((offer: HotelOffer, index) => (
                    <HotelResultItem
                        key={offer.offerId}
                        offer={offer}
                        hotel={hotel}
                        index={index}
                        paxEnabled={paxEnabled}
                        filterNumberOfAdults={filters.numberOfAdults}
                        filterAgesOfChildren={filters.agesOfChildren}
                    />
                ))
            )}

            {offersLeft > 0 && (
                <Button link className="font-bold flex mx-auto" onClick={fetchMoreOffers} disabled={isPending}>
                    Last inn flere tilbud
                </Button>
            )}
        </div>
    );
};
